import { useEffect, useState } from "react";
import { FeedbackApi, ManagerApi, ReleaseApi } from "../api";
import {
  FeedbackData,
  GENERAL_THREAD,
  ManagerMessageData,
  ManagerThreadData,
  MESSAGE_FROM,
  MESSAGE_TEXT_MAX,
  RELEASE_STATUS,
  ReleaseData,
} from "../../../types";
import { parseNotes, releasesWithNotes } from "../releaseNotes";
import { describeOutcome, markReleased, releasedCandidates } from "../markReleased";

const RECENT_MESSAGES = 10;

const TERMINAL: string[] = [RELEASE_STATUS.TAGGED, RELEASE_STATUS.CANCELLED];

// Most recent live release, else the latest of any (releases arrive newest first).
const pickCurrent = (releases: ReleaseData[]) =>
  releases.find((r) => !TERMINAL.includes(r.status)) ?? releases[0];

// Invisible until the release list loads — non-admins get a failure, same
// pattern as AdminFeedbackPanel.
export const ReleaseManagerPanel = () => {
  const [releases, setReleases] = useState<ReleaseData[] | null>(null);
  const [feedback, setFeedback] = useState<FeedbackData[]>([]);
  const [open, setOpen] = useState(false);
  const [threadId, setThreadId] = useState(GENERAL_THREAD);
  const [thread, setThread] = useState<ManagerThreadData | null>(null);
  const [messages, setMessages] = useState<ManagerMessageData[]>([]);
  // The general thread's state, kept for the header badge even while another thread is open.
  const [generalThread, setGeneralThread] = useState<ManagerThreadData | null>(null);
  const [generalMessages, setGeneralMessages] = useState<ManagerMessageData[]>([]);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [showAllMessages, setShowAllMessages] = useState(false);
  // The release whose bulk "mark released" is in flight (one at a time), and
  // the last outcome line per release id — kept across the reload that follows.
  const [releasingId, setReleasingId] = useState<string | null>(null);
  const [releaseOutcome, setReleaseOutcome] = useState<Record<string, string>>({});

  const loadThread = async (id: string) => {
    const result = await ManagerApi.getThread(id);
    if (!result.success || !result.data) return;
    setThread(result.data.thread);
    setMessages(result.data.messages);
    if (id === GENERAL_THREAD) {
      setGeneralThread(result.data.thread);
      setGeneralMessages(result.data.messages);
    }
  };

  const load = async () => {
    setBusy(true);
    const [rel, fb] = await Promise.all([ReleaseApi.getAll(), FeedbackApi.getLog()]);
    if (rel.success && rel.data) setReleases(rel.data.releases);
    if (fb.success && fb.data) setFeedback(fb.data.feedback);
    await loadThread(open ? threadId : GENERAL_THREAD);
    setBusy(false);
  };

  useEffect(() => {
    load();
  }, []);

  const hasUnread = (() => {
    const lastManager = [...generalMessages].reverse().find((m) => m.from === MESSAGE_FROM.MANAGER);
    return (
      !!lastManager &&
      (!generalThread?.lastSeenBySamAt || lastManager.createdAt > generalThread.lastSeenBySamAt)
    );
  })();

  const openThread = async (id: string) => {
    setThreadId(id);
    setShowAllMessages(false);
    await loadThread(id);
    await ManagerApi.markSeen(id);
    const stamp = new Date().toISOString();
    setThread((t) => (t ? { ...t, lastSeenBySamAt: stamp } : t));
    if (id === GENERAL_THREAD) setGeneralThread((t) => (t ? { ...t, lastSeenBySamAt: stamp } : t));
  };

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next) openThread(threadId);
  };

  if (releases === null) return null;

  const current = pickCurrent(releases);
  const releaseItems = current
    ? feedback.filter((f) => f.releaseId === current.id || current.itemIds.includes(f.id))
    : [];
  const threadOptions = [
    GENERAL_THREAD,
    ...releases.filter((r) => !TERMINAL.includes(r.status)).map((r) => r.id),
  ];
  const pastReleases = releases
    .filter((r) => r.status === RELEASE_STATUS.TAGGED && r.id !== current?.id)
    .sort((a, b) => (b.taggedAt ?? "").localeCompare(a.taggedAt ?? ""));
  // Tagged releases that still carry merged items: Sam's prod deploy of the tag
  // is what moves them to "released", and this is where he does it in one go.
  // (The feedback log is capped per status, so a very old merged item could be
  // missing from the count; the per-item buttons in the feedback log remain.)
  const awaitingProd = releases
    .filter((r) => r.status === RELEASE_STATUS.TAGGED)
    .map((r) => ({ release: r, items: releasedCandidates(r, feedback) }))
    .filter((entry) => entry.items.length > 0)
    .sort((a, b) => (b.release.taggedAt ?? "").localeCompare(a.release.taggedAt ?? ""));
  const notedReleases = releasesWithNotes(releases);
  const hiddenCount = showAllMessages ? 0 : Math.max(0, messages.length - RECENT_MESSAGES);
  const visibleMessages = hiddenCount ? messages.slice(-RECENT_MESSAGES) : messages;
  const threadLabel = (id: string) =>
    id === GENERAL_THREAD ? "General" : (releases.find((r) => r.id === id)?.version ?? id);

  const approve = async (release: ReleaseData) => {
    const infraWarning = release.infraNotes?.trim()
      ? `\n\nWARNING — this release lists infra changes:\n${release.infraNotes}\n`
      : "";
    const ok = window.confirm(
      `Approve ${release.version} for a prod tag?\n\nThe tag will point at commit ${release.releaseSha ?? "(none)"}.${infraWarning}`,
    );
    if (!ok) return;
    const result = await ReleaseApi.update({
      id: release.id,
      status: RELEASE_STATUS.APPROVED,
      expectedStatus: RELEASE_STATUS.ON_DEV,
    });
    if (!result.success) alert(result.error ?? "Couldn't approve — reloading.");
    load();
  };

  // After Sam's prod deploy of a tagged release: every merged item in it goes
  // to "released" (users see "Ready to try"). One guarded PATCH per item,
  // bounded by the release's item list — there is no batch endpoint — and a
  // stale item surfaces as a 409 in the outcome line rather than being skipped.
  const releaseToProd = async (release: ReleaseData, items: FeedbackData[]) => {
    if (releasingId) return;
    const ok = window.confirm(
      `Mark ${items.length} merged item${items.length === 1 ? "" : "s"} in ${release.version} as released?\n\nOnly do this once ${release.tag ?? release.version} is actually deployed to prod. Any item that changed since this list loaded is reported, not changed.`,
    );
    if (!ok) return;
    setReleasingId(release.id);
    const outcome = await markReleased(items, FeedbackApi.update);
    setReleaseOutcome((o) => ({ ...o, [release.id]: describeOutcome(outcome) }));
    setReleasingId(null);
    load();
  };

  const send = async () => {
    const text = draft.trim();
    if (!text) return;
    const result = await ManagerApi.sendMessage({ id: crypto.randomUUID(), threadId, text });
    if (result.success) {
      setDraft("");
      await loadThread(threadId);
    } else {
      alert(result.error ?? "Couldn't send.");
    }
  };

  return (
    <section className="pt-4 border-t">
      <div className="flex flex-wrap items-center gap-x-3">
        <button onClick={toggle} className="min-h-10 sm:min-h-0 text-sm font-semibold text-neutral-500 text-left">
          Release manager
          {hasUnread && (
            <span className="ml-2 text-[10px] bg-amber-100 text-amber-800 rounded-full px-2 py-0.5">
              new message
            </span>
          )}
        </button>
        <button onClick={load} disabled={busy} className="min-h-10 sm:min-h-0 text-xs text-neutral-400 disabled:opacity-50">
          {busy ? "Refreshing…" : "Refresh"}
        </button>
      </div>

      {open && (
        <div className="mt-2 space-y-3">
          {current ? (
            <div className="bg-white border rounded px-3 py-2 text-sm space-y-2">
              <div className="flex items-start justify-between gap-2">
                <span className="min-w-0 break-words font-semibold">
                  {current.version} — {current.title}
                </span>
                <span className="text-xs text-neutral-400 capitalize whitespace-nowrap">{current.status}</span>
              </div>

              {current.infraNotes?.trim() && (
                <div className="border-2 border-red-400 bg-red-50 text-red-900 rounded px-2 py-1 text-xs">
                  <p className="font-semibold">Infra changes needed</p>
                  <p className="whitespace-pre-wrap break-words">{current.infraNotes}</p>
                </div>
              )}

              <p className="text-xs text-neutral-600 whitespace-pre-wrap break-words">{current.plan}</p>

              {releaseItems.length > 0 && (
                <ul className="space-y-0.5">
                  {releaseItems.map((item) => (
                    <li key={item.id} className="text-xs flex justify-between gap-2">
                      <span className="truncate">{item.text}</span>
                      <span className="text-neutral-400 capitalize whitespace-nowrap">{item.status}</span>
                    </li>
                  ))}
                </ul>
              )}

              {current.releaseSha && (
                <p className="text-xs text-neutral-400 font-mono">commit: {current.releaseSha.slice(0, 10)}</p>
              )}

              {current.status === RELEASE_STATUS.ON_DEV && (
                <button
                  onClick={() => approve(current)}
                  className="min-h-10 sm:min-h-0 text-xs bg-neutral-900 text-white rounded px-3 py-1"
                >
                  Approve for prod tag
                </button>
              )}
              {current.status === RELEASE_STATUS.APPROVED && (
                <p className="text-xs text-neutral-500">
                  Approved{current.approvedAt ? ` ${new Date(current.approvedAt).toLocaleString()}` : ""} — waiting
                  for the manager to tag it.
                </p>
              )}
              {current.status === RELEASE_STATUS.TAGGED && (
                <p className="text-xs text-neutral-500">
                  Tagged {current.tag}
                  {current.taggedAt ? ` at ${new Date(current.taggedAt).toLocaleString()}` : ""}.
                </p>
              )}
            </div>
          ) : (
            <p className="text-xs text-neutral-400">No releases yet.</p>
          )}

          {(awaitingProd.length > 0 || Object.keys(releaseOutcome).length > 0) && (
            <div className="space-y-2">
              <p className="text-xs font-semibold text-neutral-500">Tagged, awaiting prod ({awaitingProd.length})</p>
              {awaitingProd.length === 0 && (
                <p className="text-xs text-neutral-400">Nothing left to mark — every tagged release's items are released.</p>
              )}
              <ul className="space-y-1">
                {awaitingProd.map(({ release, items }) => (
                  <li key={release.id} className="bg-white border rounded px-3 py-2 text-xs space-y-1">
                    <p className="font-semibold break-words">
                      {release.version} — {release.title}
                      <span className="font-normal text-neutral-400">
                        {" "}· {items.length} merged item{items.length === 1 ? "" : "s"}
                      </span>
                    </p>
                    <button
                      onClick={() => releaseToProd(release, items)}
                      disabled={releasingId !== null}
                      className="min-h-10 sm:min-h-0 bg-neutral-900 text-white rounded px-3 py-1 disabled:opacity-50"
                    >
                      {releasingId === release.id
                        ? "Marking…"
                        : `Mark all ${items.length} merged item${items.length === 1 ? "" : "s"} in ${release.version} as released`}
                    </button>
                    {releaseOutcome[release.id] && (
                      <p aria-live="polite" className="text-neutral-500 break-words">
                        {releaseOutcome[release.id]}
                      </p>
                    )}
                  </li>
                ))}
              </ul>
              {Object.entries(releaseOutcome)
                .filter(([id]) => !awaitingProd.some((entry) => entry.release.id === id))
                .map(([id, line]) => (
                  <p key={id} aria-live="polite" className="text-xs text-neutral-500 break-words">
                    {releases.find((r) => r.id === id)?.version ?? id}: {line}
                  </p>
                ))}
            </div>
          )}

          {pastReleases.length > 0 && (
            <details className="text-xs">
              <summary className="cursor-pointer text-neutral-500">Past releases ({pastReleases.length})</summary>
              <ul className="mt-1 space-y-1">
                {pastReleases.map((r) => (
                  <li key={r.id} className="bg-white border rounded px-3 py-2 break-words">
                    <p className="font-semibold">
                      {r.version} — {r.title}
                    </p>
                    <p className="text-neutral-400">
                      {r.tag}
                      {r.taggedAt ? ` · ${new Date(r.taggedAt).toLocaleString()}` : ""}
                      {r.releaseSha ? ` · ` : ""}
                      {r.releaseSha && <span className="font-mono">{r.releaseSha.slice(0, 8)}</span>}
                    </p>
                  </li>
                ))}
              </ul>
            </details>
          )}

          <div className="space-y-2">
            <p className="text-xs font-semibold text-neutral-500">Release notes</p>
            {notedReleases.length === 0 ? (
              <p className="text-xs text-neutral-400">No release notes yet — they appear here once a release is tagged.</p>
            ) : (
              <ul className="space-y-1">
                {notedReleases.map((r) => (
                  <li key={r.id} className="bg-white border rounded px-3 py-2 text-xs space-y-1">
                    <p className="font-semibold">
                      {r.version}
                      {r.taggedAt ? (
                        <span className="font-normal text-neutral-400"> · {new Date(r.taggedAt).toLocaleDateString()}</span>
                      ) : null}
                    </p>
                    {parseNotes(r.publicNotes ?? "").map((block, i) =>
                      block.kind === "list" ? (
                        <ul key={i} className="list-disc pl-4">
                          {block.items.map((item, j) => (
                            <li key={j}>{item}</li>
                          ))}
                        </ul>
                      ) : (
                        <p key={i}>{block.text}</p>
                      ),
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="space-y-2">
            <p className="text-xs font-semibold text-neutral-500">Conversation</p>
            <p className="text-xs text-neutral-400">
              General is the standing conversation with the manager and keeps all history. A release tab holds
              messages about just that release.
            </p>
            <div className="flex flex-wrap gap-x-2 text-xs">
              {threadOptions.map((id) => (
                <button
                  key={id}
                  onClick={() => openThread(id)}
                  className={`min-h-10 sm:min-h-0 ${threadId === id ? "font-semibold" : "text-neutral-400"}`}
                >
                  {threadLabel(id)}
                </button>
              ))}
            </div>

            {hiddenCount > 0 && (
              <button onClick={() => setShowAllMessages(true)} className="min-h-10 sm:min-h-0 text-xs text-neutral-500">
                Show {hiddenCount} earlier
              </button>
            )}
            <ul className="space-y-1">
              {visibleMessages.map((m) => (
                <li key={m.id} className="bg-white border rounded px-3 py-2 text-sm">
                  <p className="text-xs text-neutral-400">
                    {m.from} · {new Date(m.createdAt).toLocaleString()}
                  </p>
                  <p className="whitespace-pre-wrap break-words">{m.text}</p>
                </li>
              ))}
              {messages.length === 0 && <li className="text-xs text-neutral-400">No messages yet.</li>}
            </ul>
            {thread?.summary && (
              <p className="text-xs text-neutral-400 italic">Earlier messages are folded into a summary.</p>
            )}

            <div className="flex gap-2 items-start">
              <textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder="Message the release manager…"
                maxLength={MESSAGE_TEXT_MAX}
                rows={2}
                className="min-w-0 flex-1 border rounded px-2 py-1 text-xs"
              />
              <button onClick={send} disabled={!draft.trim()} className="min-h-10 sm:min-h-0 text-xs text-neutral-500 disabled:opacity-40">
                Send
              </button>
            </div>
            <p className="text-xs text-neutral-400">
              The manager reads messages on its next scheduled run (roughly daily), so replies aren't instant.
            </p>
          </div>
        </div>
      )}
    </section>
  );
};
